import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Activity,
  TrendingUp,
  Clock,
  AlertTriangle,
  Server,
  Zap,
  BarChart3,
  RefreshCw,
  Calendar,
  Globe,
  ArrowUpRight,
  ArrowDownRight,
  Download,
  Trash2,
  Database,
  FileSpreadsheet,
  HardDrive,
  Mail,
  Bell,
  Settings
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Simple chart component for hourly traffic
const TrafficChart = ({ data }) => {
  if (!data || data.length === 0) return null;
  
  const maxCount = Math.max(...data.map(d => d.count), 1);
  
  return (
    <div className="flex items-end gap-1 h-32">
      {data.map((item) => {
        const height = (item.count / maxCount) * 100;
        const hour = item.hour.split('-').slice(-1)[0];
        return (
          <div
            key={item.hour}
            className="flex-1 flex flex-col items-center gap-1"
            title={`${item.hour}: ${item.count} requests`}
          >
            <div
              className="w-full bg-gradient-to-t from-sky-500 to-sky-400 rounded-t transition-all hover:from-sky-400 hover:to-sky-300"
              style={{ height: `${Math.max(height, 2)}%` }}
            />
            <span className="text-[10px] text-slate-500">{hour}h</span>
          </div>
        );
      })}
    </div>
  );
};

// Status code badge
const StatusBadge = ({ code, count }) => {
  const getColor = () => {
    if (code.startsWith('2')) return 'bg-green-500/20 text-green-400 border-green-500/30';
    if (code.startsWith('3')) return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
    if (code.startsWith('4')) return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
    if (code.startsWith('5')) return 'bg-red-500/20 text-red-400 border-red-500/30';
    return 'bg-slate-500/20 text-slate-400 border-slate-500/30';
  };
  
  return (
    <div className={`px-3 py-1.5 rounded-lg border ${getColor()} flex items-center gap-2`}>
      <span className="font-mono font-bold">{code}</span>
      <span className="text-xs opacity-75">{count.toLocaleString()}</span>
    </div>
  );
};

// Stat card component
const StatCard = ({ icon: Icon, label, value, subValue, trend, color = 'sky' }) => {
  const colorClasses = {
    sky: 'from-sky-500 to-blue-600',
    green: 'from-green-500 to-emerald-600',
    yellow: 'from-yellow-500 to-amber-600',
    red: 'from-red-500 to-rose-600',
    purple: 'from-purple-500 to-violet-600'
  };
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-slate-900/50 border border-slate-800 rounded-xl p-5"
    >
      <div className="flex items-start justify-between mb-3">
        <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${colorClasses[color]} flex items-center justify-center`}>
          <Icon className="w-5 h-5 text-white" />
        </div>
        {trend !== undefined && (
          <div className={`flex items-center gap-1 text-xs ${trend >= 0 ? 'text-green-400' : 'text-red-400'}`}>
            {trend >= 0 ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
            {Math.abs(trend)}%
          </div>
        )}
      </div>
      <div className="text-2xl font-bold text-white mb-1">{value}</div>
      <div className="text-sm text-slate-400">{label}</div>
      {subValue && <div className="text-xs text-slate-500 mt-1">{subValue}</div>}
    </motion.div>
  );
};

export default function AnalyticsDashboard() {
  const navigate = useNavigate();
  const [analytics, setAnalytics] = useState(null);
  const [recentRequests, setRecentRequests] = useState([]);
  const [storageStats, setStorageStats] = useState(null);
  const [scheduledConfig, setScheduledConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedDays, setSelectedDays] = useState(7);
  const [activeTab, setActiveTab] = useState('overview');
  const [cleanupLoading, setCleanupLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(null);
  const [reportEmail, setReportEmail] = useState('');
  const [sendingReport, setSendingReport] = useState(false);
  const [savingConfig, setSavingConfig] = useState(false);

  const fetchAnalytics = async () => {
    try {
      const [summaryRes, requestsRes, storageRes, configRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/analytics/summary?days=${selectedDays}`),
        axios.get(`${BACKEND_URL}/api/analytics/requests?limit=20`),
        axios.get(`${BACKEND_URL}/api/analytics/storage`),
        axios.get(`${BACKEND_URL}/api/scheduled-tasks/config`)
      ]);
      
      setAnalytics(summaryRes.data);
      setRecentRequests(requestsRes.data.requests || []);
      setStorageStats(storageRes.data);
      setScheduledConfig(configRes.data);
      if (configRes.data?.email_reports?.recipient_email) {
        setReportEmail(configRes.data.email_reports.recipient_email);
      }
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchAnalytics();
    // Auto-refresh every 30 seconds
    const interval = setInterval(fetchAnalytics, 30000);
    return () => clearInterval(interval);
  }, [selectedDays]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchAnalytics();
  };

  const handleCleanup = async (retentionDays) => {
    if (!window.confirm(`Delete all analytics data older than ${retentionDays} days? This cannot be undone.`)) {
      return;
    }
    
    setCleanupLoading(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/analytics/cleanup?retention_days=${retentionDays}`);
      const data = response.data;
      toast.success(`Cleanup complete! Deleted: ${data.deleted_logs} logs, ${data.deleted_errors} errors`);
      fetchAnalytics(); // Refresh data
    } catch (error) {
      toast.error('Cleanup failed: ' + (error.response?.data?.detail || error.message));
    } finally {
      setCleanupLoading(false);
    }
  };

  const handleExport = async (type) => {
    setExportLoading(type);
    try {
      const endpoints = {
        requests: `/api/analytics/export/requests?days=${selectedDays}`,
        errors: `/api/analytics/export/errors?days=30`,
        daily: `/api/analytics/export/daily?days=90`,
        endpoints: `/api/analytics/export/endpoints?days=30`
      };
      
      const response = await axios.get(`${BACKEND_URL}${endpoints[type]}`, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `analytics_${type}_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(`${type} data exported successfully!`);
    } catch (error) {
      toast.error('Export failed: ' + (error.response?.data?.detail || error.message));
    } finally {
      setExportLoading(null);
    }
  };

  const handleSendTestReport = async () => {
    if (!reportEmail) {
      toast.error('Please enter an email address');
      return;
    }
    
    setSendingReport(true);
    try {
      await axios.post(`${BACKEND_URL}/api/scheduled-tasks/send-report?email=${encodeURIComponent(reportEmail)}`);
      toast.success(`Test report sent to ${reportEmail}`);
    } catch (error) {
      toast.error('Failed to send report: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSendingReport(false);
    }
  };

  const handleSaveEmailConfig = async (enabled) => {
    setSavingConfig(true);
    try {
      await axios.post(`${BACKEND_URL}/api/scheduled-tasks/config`, {
        email_reports_enabled: enabled,
        email_reports_recipient: reportEmail || null
      });
      toast.success(enabled ? 'Daily reports enabled' : 'Daily reports disabled');
      fetchAnalytics(); // Refresh config
    } catch (error) {
      toast.error('Failed to save config: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSavingConfig(false);
    }
  };

  const handleSaveRetentionConfig = async (days) => {
    setSavingConfig(true);
    try {
      await axios.post(`${BACKEND_URL}/api/scheduled-tasks/config`, {
        cleanup_retention_days: days
      });
      toast.success(`Auto-cleanup set to ${days} days`);
      fetchAnalytics();
    } catch (error) {
      toast.error('Failed to save config: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSavingConfig(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 text-sky-400 animate-spin mx-auto mb-4" />
          <p className="text-slate-400">Loading analytics...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      {/* Header */}
      <header className="border-b border-slate-800 bg-slate-900/50 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button 
                onClick={() => navigate('/')}
                className="p-2 hover:bg-slate-800 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-sky-400 to-blue-600 flex items-center justify-center">
                  <BarChart3 className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h1 className="font-bold text-lg">API Analytics</h1>
                  <p className="text-xs text-slate-400">Real-time monitoring</p>
                </div>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              {/* Time period selector */}
              <select
                value={selectedDays}
                onChange={(e) => setSelectedDays(Number(e.target.value))}
                className="bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-sky-500"
              >
                <option value={1}>Last 24 hours</option>
                <option value={7}>Last 7 days</option>
                <option value={30}>Last 30 days</option>
              </select>
              
              <Button
                onClick={handleRefresh}
                variant="outline"
                size="sm"
                className="border-slate-700 hover:bg-slate-800"
                disabled={refreshing}
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <StatCard
            icon={Activity}
            label="Total Requests"
            value={analytics?.total_requests?.toLocaleString() || '0'}
            subValue={`Last ${selectedDays} days`}
            color="sky"
          />
          <StatCard
            icon={Clock}
            label="Avg Response Time"
            value={`${analytics?.avg_response_time_ms || 0}ms`}
            color="green"
          />
          <StatCard
            icon={AlertTriangle}
            label="Error Rate"
            value={`${analytics?.error_rate_percent || 0}%`}
            color={analytics?.error_rate_percent > 5 ? 'red' : 'yellow'}
          />
          <StatCard
            icon={Zap}
            label="Endpoints"
            value={analytics?.top_endpoints?.length || 0}
            subValue="Active endpoints"
            color="purple"
          />
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b border-slate-800 pb-4">
          {['overview', 'endpoints', 'errors', 'requests', 'storage'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 rounded-lg capitalize transition-colors ${
                activeTab === tab
                  ? 'bg-sky-500/20 text-sky-400 border border-sky-500/30'
                  : 'text-slate-400 hover:bg-slate-800'
              }`}
            >
              {tab}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        {activeTab === 'overview' && (
          <div className="grid md:grid-cols-2 gap-6">
            {/* Hourly Traffic */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-slate-900/50 border border-slate-800 rounded-xl p-6"
            >
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-sky-400" />
                Hourly Traffic (24h)
              </h3>
              <TrafficChart data={analytics?.hourly_traffic || []} />
            </motion.div>

            {/* Status Codes */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-slate-900/50 border border-slate-800 rounded-xl p-6"
            >
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <Server className="w-5 h-5 text-sky-400" />
                Response Status Codes
              </h3>
              <div className="flex flex-wrap gap-2">
                {Object.entries(analytics?.status_codes || {}).map(([code, count]) => (
                  <StatusBadge key={code} code={code} count={count} />
                ))}
              </div>
            </motion.div>

            {/* Methods Distribution */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-slate-900/50 border border-slate-800 rounded-xl p-6"
            >
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <Globe className="w-5 h-5 text-sky-400" />
                HTTP Methods
              </h3>
              <div className="space-y-3">
                {Object.entries(analytics?.methods || {}).map(([method, count]) => {
                  const total = analytics?.total_requests || 1;
                  const percent = ((count / total) * 100).toFixed(1);
                  return (
                    <div key={method} className="flex items-center gap-3">
                      <span className="w-16 font-mono text-sm text-slate-400">{method}</span>
                      <div className="flex-1 h-2 bg-slate-800 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-sky-500 to-blue-500 rounded-full"
                          style={{ width: `${percent}%` }}
                        />
                      </div>
                      <span className="text-sm text-slate-400 w-20 text-right">
                        {count.toLocaleString()} ({percent}%)
                      </span>
                    </div>
                  );
                })}
              </div>
            </motion.div>

            {/* Slowest Endpoints */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-slate-900/50 border border-slate-800 rounded-xl p-6"
            >
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <Clock className="w-5 h-5 text-yellow-400" />
                Slowest Endpoints
              </h3>
              <div className="space-y-3">
                {(analytics?.slowest_endpoints || []).map((ep) => (
                  <div key={ep.endpoint} className="flex items-center justify-between p-3 bg-slate-800/50 rounded-lg">
                    <code className="text-sm text-slate-300 truncate max-w-[60%]">{ep.endpoint}</code>
                    <div className="flex items-center gap-3">
                      <span className="text-yellow-400 font-mono text-sm">{ep.avg_ms}ms</span>
                      {ep.max_ms && (
                        <span className="text-xs text-slate-500">max: {ep.max_ms}ms</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        )}

        {activeTab === 'endpoints' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-slate-900/50 border border-slate-800 rounded-xl overflow-hidden"
          >
            <div className="p-4 border-b border-slate-800">
              <h3 className="text-lg font-semibold">Top Endpoints</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-800/50">
                  <tr>
                    <th className="text-left p-4 text-sm font-medium text-slate-400">Endpoint</th>
                    <th className="text-right p-4 text-sm font-medium text-slate-400">Requests</th>
                    <th className="text-right p-4 text-sm font-medium text-slate-400">Avg Time</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800">
                  {(analytics?.top_endpoints || []).map((ep) => (
                    <tr key={ep.endpoint} className="hover:bg-slate-800/30">
                      <td className="p-4">
                        <code className="text-sm text-sky-400">{ep.endpoint}</code>
                      </td>
                      <td className="p-4 text-right text-slate-300">{ep.count.toLocaleString()}</td>
                      <td className="p-4 text-right">
                        <span className={`font-mono ${ep.avg_ms > 500 ? 'text-yellow-400' : 'text-green-400'}`}>
                          {ep.avg_ms}ms
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </motion.div>
        )}

        {activeTab === 'errors' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-slate-900/50 border border-slate-800 rounded-xl overflow-hidden"
          >
            <div className="p-4 border-b border-slate-800">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-red-400" />
                Recent Errors
              </h3>
            </div>
            {(analytics?.recent_errors || []).length === 0 ? (
              <div className="p-8 text-center text-slate-500">
                <AlertTriangle className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No recent errors</p>
              </div>
            ) : (
              <div className="divide-y divide-slate-800">
                {(analytics?.recent_errors || []).map((error) => (
                  <div key={`${error.timestamp}-${error.endpoint}-${error.status}`} className="p-4 hover:bg-slate-800/30">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className="px-2 py-0.5 bg-red-500/20 text-red-400 rounded text-xs font-mono">
                          {error.status}
                        </span>
                        <span className="text-xs text-slate-500">{error.method}</span>
                      </div>
                      <span className="text-xs text-slate-500">
                        {error.timestamp ? new Date(error.timestamp).toLocaleString() : 'N/A'}
                      </span>
                    </div>
                    <code className="text-sm text-slate-300 block mb-1">{error.endpoint}</code>
                    {error.error && (
                      <p className="text-xs text-red-400/70 truncate">{error.error}</p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </motion.div>
        )}

        {activeTab === 'requests' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-slate-900/50 border border-slate-800 rounded-xl overflow-hidden"
          >
            <div className="p-4 border-b border-slate-800">
              <h3 className="text-lg font-semibold">Recent Requests</h3>
            </div>
            <div className="overflow-x-auto max-h-[600px] overflow-y-auto">
              <table className="w-full">
                <thead className="bg-slate-800/50 sticky top-0">
                  <tr>
                    <th className="text-left p-3 text-xs font-medium text-slate-400">Time</th>
                    <th className="text-left p-3 text-xs font-medium text-slate-400">Method</th>
                    <th className="text-left p-3 text-xs font-medium text-slate-400">Endpoint</th>
                    <th className="text-right p-3 text-xs font-medium text-slate-400">Status</th>
                    <th className="text-right p-3 text-xs font-medium text-slate-400">Duration</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800">
                  {recentRequests.map((req) => (
                    <tr key={`${req.timestamp}-${req.endpoint}-${req.method}`} className="hover:bg-slate-800/30 text-sm">
                      <td className="p-3 text-slate-500 whitespace-nowrap">
                        {req.timestamp ? new Date(req.timestamp).toLocaleTimeString() : 'N/A'}
                      </td>
                      <td className="p-3">
                        <span className={`px-2 py-0.5 rounded text-xs font-mono ${
                          req.method === 'GET' ? 'bg-green-500/20 text-green-400' :
                          req.method === 'POST' ? 'bg-blue-500/20 text-blue-400' :
                          req.method === 'PUT' ? 'bg-yellow-500/20 text-yellow-400' :
                          req.method === 'DELETE' ? 'bg-red-500/20 text-red-400' :
                          'bg-slate-500/20 text-slate-400'
                        }`}>
                          {req.method}
                        </span>
                      </td>
                      <td className="p-3">
                        <code className="text-sky-400 text-xs">{req.endpoint}</code>
                      </td>
                      <td className="p-3 text-right">
                        <span className={`font-mono ${
                          req.status < 300 ? 'text-green-400' :
                          req.status < 400 ? 'text-blue-400' :
                          req.status < 500 ? 'text-yellow-400' :
                          'text-red-400'
                        }`}>
                          {req.status}
                        </span>
                      </td>
                      <td className="p-3 text-right">
                        <span className={`font-mono ${req.duration_ms > 500 ? 'text-yellow-400' : 'text-slate-400'}`}>
                          {req.duration_ms}ms
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </motion.div>
        )}

        {activeTab === 'storage' && (
          <div className="space-y-6">
            {/* Storage Stats */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-slate-900/50 border border-slate-800 rounded-xl p-6"
            >
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <Database className="w-5 h-5 text-sky-400" />
                Storage Statistics
              </h3>
              
              {storageStats ? (
                <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div className="bg-slate-800/50 rounded-lg p-4">
                    <div className="text-2xl font-bold text-white">
                      {storageStats.collections?.api_request_logs?.toLocaleString() || 0}
                    </div>
                    <div className="text-sm text-slate-400">Request Logs</div>
                  </div>
                  <div className="bg-slate-800/50 rounded-lg p-4">
                    <div className="text-2xl font-bold text-white">
                      {storageStats.collections?.api_errors?.toLocaleString() || 0}
                    </div>
                    <div className="text-sm text-slate-400">Error Logs</div>
                  </div>
                  <div className="bg-slate-800/50 rounded-lg p-4">
                    <div className="text-2xl font-bold text-white">
                      {storageStats.collections?.api_hourly_traffic?.toLocaleString() || 0}
                    </div>
                    <div className="text-sm text-slate-400">Hourly Records</div>
                  </div>
                  <div className="bg-slate-800/50 rounded-lg p-4">
                    <div className="text-2xl font-bold text-purple-400">
                      {storageStats.total_documents?.toLocaleString() || 0}
                    </div>
                    <div className="text-sm text-slate-400">Total Documents</div>
                  </div>
                </div>
              ) : (
                <p className="text-slate-500">Loading storage stats...</p>
              )}
              
              {storageStats?.date_range && (
                <div className="mt-4 text-sm text-slate-500">
                  Data range: {storageStats.date_range.oldest ? new Date(storageStats.date_range.oldest).toLocaleDateString() : 'N/A'} 
                  {' — '} 
                  {storageStats.date_range.newest ? new Date(storageStats.date_range.newest).toLocaleDateString() : 'N/A'}
                </div>
              )}
            </motion.div>

            {/* Export Section */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-slate-900/50 border border-slate-800 rounded-xl p-6"
            >
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <FileSpreadsheet className="w-5 h-5 text-green-400" />
                Export Data (CSV)
              </h3>
              
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                <button
                  onClick={() => handleExport('requests')}
                  disabled={exportLoading === 'requests'}
                  className="flex items-center justify-center gap-2 p-4 bg-slate-800/50 hover:bg-slate-800 rounded-lg transition-colors disabled:opacity-50"
                >
                  {exportLoading === 'requests' ? (
                    <RefreshCw className="w-4 h-4 animate-spin" />
                  ) : (
                    <Download className="w-4 h-4 text-sky-400" />
                  )}
                  <span>Request Logs</span>
                </button>
                
                <button
                  onClick={() => handleExport('errors')}
                  disabled={exportLoading === 'errors'}
                  className="flex items-center justify-center gap-2 p-4 bg-slate-800/50 hover:bg-slate-800 rounded-lg transition-colors disabled:opacity-50"
                >
                  {exportLoading === 'errors' ? (
                    <RefreshCw className="w-4 h-4 animate-spin" />
                  ) : (
                    <Download className="w-4 h-4 text-red-400" />
                  )}
                  <span>Error Logs</span>
                </button>
                
                <button
                  onClick={() => handleExport('daily')}
                  disabled={exportLoading === 'daily'}
                  className="flex items-center justify-center gap-2 p-4 bg-slate-800/50 hover:bg-slate-800 rounded-lg transition-colors disabled:opacity-50"
                >
                  {exportLoading === 'daily' ? (
                    <RefreshCw className="w-4 h-4 animate-spin" />
                  ) : (
                    <Download className="w-4 h-4 text-purple-400" />
                  )}
                  <span>Daily Stats</span>
                </button>
                
                <button
                  onClick={() => handleExport('endpoints')}
                  disabled={exportLoading === 'endpoints'}
                  className="flex items-center justify-center gap-2 p-4 bg-slate-800/50 hover:bg-slate-800 rounded-lg transition-colors disabled:opacity-50"
                >
                  {exportLoading === 'endpoints' ? (
                    <RefreshCw className="w-4 h-4 animate-spin" />
                  ) : (
                    <Download className="w-4 h-4 text-green-400" />
                  )}
                  <span>Endpoint Stats</span>
                </button>
              </div>
              
              <p className="text-xs text-slate-500 mt-4">
                Exports include data from the selected time period. Files are downloaded in CSV format.
              </p>
            </motion.div>

            {/* Cleanup Section */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-slate-900/50 border border-red-900/30 rounded-xl p-6"
            >
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <Trash2 className="w-5 h-5 text-red-400" />
                Data Retention & Cleanup
              </h3>
              
              <p className="text-slate-400 mb-4">
                Remove old analytics data to free up storage. This action cannot be undone.
              </p>
              
              <div className="flex flex-wrap gap-3">
                <Button
                  onClick={() => handleCleanup(7)}
                  disabled={cleanupLoading}
                  variant="outline"
                  className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                >
                  {cleanupLoading ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
                  Keep 7 Days
                </Button>
                
                <Button
                  onClick={() => handleCleanup(30)}
                  disabled={cleanupLoading}
                  variant="outline"
                  className="border-yellow-500/30 text-yellow-400 hover:bg-yellow-500/10"
                >
                  {cleanupLoading ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
                  Keep 30 Days
                </Button>
                
                <Button
                  onClick={() => handleCleanup(90)}
                  disabled={cleanupLoading}
                  variant="outline"
                  className="border-slate-500/30 text-slate-400 hover:bg-slate-500/10"
                >
                  {cleanupLoading ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
                  Keep 90 Days
                </Button>
              </div>
              
              <p className="text-xs text-slate-600 mt-4">
                Data older than the selected period will be permanently deleted from all analytics collections.
              </p>
            </motion.div>

            {/* Email Reports Section */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-slate-900/50 border border-slate-800 rounded-xl p-6"
            >
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <Mail className="w-5 h-5 text-blue-400" />
                Daily Email Reports
              </h3>
              
              <p className="text-slate-400 mb-4">
                Receive automated daily analytics reports via email at 8 AM UTC.
              </p>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm text-slate-400 mb-2">Recipient Email</label>
                  <div className="flex gap-2">
                    <input
                      type="email"
                      value={reportEmail}
                      onChange={(e) => setReportEmail(e.target.value)}
                      placeholder="your@email.com"
                      className="flex-1 px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-sky-500"
                    />
                    <Button
                      onClick={handleSendTestReport}
                      disabled={sendingReport || !reportEmail}
                      variant="outline"
                      className="border-blue-500/30 text-blue-400 hover:bg-blue-500/10"
                    >
                      {sendingReport ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Mail className="w-4 h-4 mr-2" />}
                      Test
                    </Button>
                  </div>
                </div>
                
                <div className="flex items-center justify-between pt-4 border-t border-slate-800">
                  <div className="flex items-center gap-2">
                    <Bell className="w-4 h-4 text-slate-400" />
                    <span className="text-sm text-slate-400">
                      Daily reports: {scheduledConfig?.email_reports?.enabled ? (
                        <span className="text-green-400">Enabled</span>
                      ) : (
                        <span className="text-slate-500">Disabled</span>
                      )}
                    </span>
                  </div>
                  
                  <div className="flex gap-2">
                    {scheduledConfig?.email_reports?.enabled ? (
                      <Button
                        onClick={() => handleSaveEmailConfig(false)}
                        disabled={savingConfig}
                        variant="outline"
                        size="sm"
                        className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                      >
                        Disable Reports
                      </Button>
                    ) : (
                      <Button
                        onClick={() => handleSaveEmailConfig(true)}
                        disabled={savingConfig || !reportEmail}
                        size="sm"
                        className="bg-blue-500 hover:bg-blue-600"
                      >
                        Enable Reports
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Auto Cleanup Config */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="bg-slate-900/50 border border-slate-800 rounded-xl p-6"
            >
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <Settings className="w-5 h-5 text-slate-400" />
                Automatic Cleanup Schedule
              </h3>
              
              <p className="text-slate-400 mb-4">
                Automatically clean old analytics data daily. Current setting: 
                <span className="text-white font-medium ml-1">
                  {scheduledConfig?.cleanup?.retention_days || 30} days
                </span>
              </p>
              
              <div className="flex flex-wrap gap-2">
                {[7, 14, 30, 60, 90].map((days) => (
                  <Button
                    key={days}
                    onClick={() => handleSaveRetentionConfig(days)}
                    disabled={savingConfig}
                    variant="outline"
                    size="sm"
                    className={`${
                      scheduledConfig?.cleanup?.retention_days === days
                        ? 'border-sky-500 text-sky-400 bg-sky-500/10'
                        : 'border-slate-700 text-slate-400 hover:bg-slate-800'
                    }`}
                  >
                    {days} days
                  </Button>
                ))}
              </div>
              
              {/* Recent Task Logs */}
              {scheduledConfig?.recent_logs?.length > 0 && (
                <div className="mt-6 pt-4 border-t border-slate-800">
                  <h4 className="text-sm font-medium text-slate-400 mb-3">Recent Scheduled Tasks</h4>
                  <div className="space-y-2">
                    {scheduledConfig.recent_logs.slice(0, 5).map((log) => (
                      <div key={`${log.timestamp}-${log.task}`} className="flex items-center justify-between text-xs bg-slate-800/50 rounded p-2">
                        <span className="text-slate-400">
                          {log.task === 'analytics_cleanup' ? '🧹 Cleanup' : '📧 Report'}
                        </span>
                        <span className="text-slate-500">
                          {log.timestamp ? new Date(log.timestamp).toLocaleString() : 'N/A'}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </motion.div>
          </div>
        )}

        {/* Footer */}
        <div className="mt-8 text-center text-xs text-slate-600">
          <p>Data refreshes automatically every 30 seconds</p>
          {analytics?.generated_at && (
            <p className="mt-1">Last updated: {new Date(analytics.generated_at).toLocaleString()}</p>
          )}
        </div>
      </main>
    </div>
  );
}
